import {
    Button,
    Layout,
    Loading,
    Modal,
    Paper,
    Switch,
    Typography,
    useAlert
} from "matsuri-ui"
import { useAuth } from "m2m-components/storage/useM2mAuth"
import {
    ChangeEvent,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from "react"
import { Navigate, useNavigate, useParams } from "react-router-dom"
import dayjs from "dayjs"
import { css } from "@emotion/react"
import styled from "@emotion/styled"
import CloseIcon from "@mui/icons-material/Close"
import ApartmentRoundedIcon from "@mui/icons-material/ApartmentRounded"
import AssignmentRoundedIcon from "@mui/icons-material/AssignmentRounded"
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded"
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded"
import AddRoundedIcon from "@mui/icons-material/AddRounded"
import { Header } from "@/components/Header/Header"
import { MobileAppHeader } from "@/components/Header/MobileAppHeader"
import { ReportFooter } from "@/components/Footer/ReportFooter"
import { GarbageStorageKeyInfo } from "@/pages/CleaningReportPage/parts/GarbageStorageKeyInfo"
import {
    ImageViewerOverlay,
    useImageViewerOverlay
} from "@/pages/OperationInfoPage/parts/ImageViewerOverlay"
import { BackToDetailConfirmDialog } from "@/components/Dialog/BackToDetailConfirmDialog"
import { assertIsDefined } from "@/utils/assetIsDefined"
import { genCleaningReportPagePath } from "@/routes/protected"
import { useCleaning } from "@/hooks/useCleaning"
import { useGarbageCheck } from "@/hooks/useGarbageCheck"
import { useImmediatePhotoUpload } from "@/hooks/useImmediatePhotoUpload"
import { useMobileLayoutProps } from "@/hooks/useMobileLayoutProps"
import { useNavigateBackToDetail } from "@/hooks/useNavigateBackToDetail"
import { useTranslation } from "@/hooks/useTranslation"
import { imageCompressToWebp } from "@/utils/image"
import { theme } from "@/theme"

const PropertyBar = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 16px;
    background-color: ${theme.palette.intentions.primary.light};
    color: ${theme.palette.intentions.primary.dark};
    font-size: 15px;
    font-weight: bold;
`

const Content = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    /* 下は固定フッターのクリアランス（ResizeObserver の contentRect が
       フッターの padding を含まず paddingBottom が不足するため上乗せする） */
    padding: 16px 16px 64px;
`

const InstructionBanner = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 12px 14px;
    border: 1px solid #ffe0a3;
    border-radius: 8px;
    background-color: #fff8e1;
    color: #8a6d3b;
    font-size: 14px;
    font-weight: bold;
    line-height: 1.5;
`

const BannerLines = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`

const ReportedBadge = styled.div`
    display: inline-flex;
    align-self: flex-start;
    align-items: center;
    gap: 4px;
    padding: 4px 12px;
    border-radius: 999px;
    background-color: ${theme.palette.intentions.primary.light};
    color: ${theme.palette.intentions.primary.dark};
    font-size: 12px;
    font-weight: bold;
`

const Card = styled(Paper)`
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 16px;
    border-radius: 12px;
`

const CardTitle = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 16px;
    font-weight: bold;
    color: ${theme.palette.intentions.text.primary};
`

const TitleAccent = styled.span`
    width: 4px;
    height: 16px;
    border-radius: 2px;
    background-color: ${theme.palette.intentions.primary.main};
`

const SampleImage = styled.img`
    width: 100%;
    max-height: 240px;
    object-fit: contain;
    border-radius: 8px;
    cursor: pointer;
`

const EmptyState = styled.div`
    display: grid;
    place-items: center;
    min-height: 96px;
    padding: 16px;
    border: 1px dashed ${theme.palette.intentions.cleanerGray.main};
    border-radius: 8px;
    background-color: ${theme.palette.intentions.cleanerBackground.main};
    color: ${theme.palette.intentions.cleanerGray.dark};
    font-size: 13px;
    text-align: center;
`

const ToggleRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`

const ToggleSubLabel = styled.span`
    font-size: 12px;
    color: ${theme.palette.intentions.cleanerGray.dark};
`

const WarningBanner = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 12px 14px;
    border-radius: 8px;
    background-color: ${theme.palette.intentions.secondary.light};
    color: ${theme.palette.intentions.secondary.dark};
    font-size: 14px;
    font-weight: bold;
    line-height: 1.5;
`

const PhotoSectionLabel = styled.div`
    font-size: 13px;
    font-weight: bold;
    color: ${theme.palette.intentions.text.primary};
`

const PhotoCount = styled.span`
    margin-left: 12px;
    font-weight: normal;
    color: ${theme.palette.intentions.cleanerGray.dark};
`

const ThumbnailGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
`

const ThumbnailWrapper = styled.div`
    position: relative;
    aspect-ratio: 1 / 1;
`

const Thumbnail = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 8px;
`

const RemoveButton = styled.button`
    position: absolute;
    top: -6px;
    right: -6px;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    border: none;
    background-color: rgba(0, 0, 0, 0.6);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
`

const StatusOverlay = styled.div`
    position: absolute;
    inset: 0;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(0, 0, 0, 0.45);
    color: #fff;
    font-size: 12px;
    text-align: center;
    white-space: pre-line;
`

const AddTile = styled.button`
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    padding: 12px;
    border: 1.5px dashed ${theme.palette.intentions.cleanerGray.main};
    border-radius: 8px;
    background-color: transparent;
    color: ${theme.palette.intentions.cleanerGray.dark};
    font-size: 13px;
    text-align: left;
    cursor: pointer;
`

const AddTileIcon = styled.span`
    flex-shrink: 0;
    width: 56px;
    height: 56px;
    display: grid;
    place-items: center;
    border: 1.5px dashed ${theme.palette.intentions.cleanerGray.main};
    border-radius: 8px;
    color: ${theme.palette.intentions.primary.main};
`

const CenteredBody = styled.div`
    display: grid;
    place-items: center;
    gap: 16px;
    min-height: 50vh;
    padding: 24px;
    text-align: center;
`

export const GarbageCheckPage = () => {
    const { t } = useTranslation()
    const { cleaningId } = useParams<"cleaningId">()
    assertIsDefined(cleaningId)

    const navigate = useNavigate()
    const { token } = useAuth()
    const { throwAlert } = useAlert()

    const { data: cleaning } = useCleaning(token, cleaningId)
    const listingName = cleaning?.cleaningInfo.listingName

    const {
        isTarget,
        sampleImageUrl,
        keyInfo,
        existingReport,
        isLoading,
        error,
        uploadImage,
        completeCheck
    } = useGarbageCheck(cleaningId)

    // 既に報告済みなら、報告日時・写真を表示する読み取り専用の表示にする。
    const alreadyReported = !!existingReport

    const { openedImage, openImageViewer, closeImageViewer } =
        useImageViewerOverlay()

    const {
        photos,
        addPhoto,
        removePhoto,
        retryPhoto,
        getReadyUploadIds,
        getHasError,
        waitAllSettled,
        reset,
        isUploading,
        hasError
    } = useImmediatePhotoUpload({ put: uploadImage })

    const [isOverflowing, setIsOverflowing] = useState(false)
    const [openConfirm, setOpenConfirm] = useState(false)
    const [submitting, setSubmitting] = useState(false)
    const [compressingPreviews, setCompressingPreviews] = useState<
        { id: string; objectURL: string }[]
    >([])
    const compressingPreviewsRef = useRef<{ id: string; objectURL: string }[]>(
        []
    )
    const previewCounterRef = useRef(0)

    useEffect(() => {
        compressingPreviewsRef.current = compressingPreviews
    }, [compressingPreviews])

    // unmount 時に圧縮中プレビューの objectURL を revoke する
    useEffect(() => {
        return () => {
            compressingPreviewsRef.current.forEach(p =>
                URL.revokeObjectURL(p.objectURL)
            )
        }
    }, [])

    const fileInputRef = useRef<HTMLInputElement>(null)

    // 清掃詳細画面に戻る確認モーダル
    const {
        confirmBackOpen,
        openConfirmBack,
        closeConfirmBack,
        goBackToDetail
    } = useNavigateBackToDetail("cleaning", cleaningId)

    const { rootProps, headerProps, bodyProps, footerProps } =
        useMobileLayoutProps()

    const mainStyle = {
        ...rootProps.style,
        backgroundColor: theme.palette.intentions.cleanerBackground.main
    }

    const handleToggle = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            const checked = event.currentTarget.checked
            setIsOverflowing(checked)
            // 溢れOFFに戻したら撮影済み写真・進行中アップロードを破棄する
            // （古い写真が残って再ONで誤送信されるのを防ぐ）
            if (!checked) {
                reset()
            }
        },
        [reset]
    )

    const handleFileChange = useCallback(
        async (event: ChangeEvent<HTMLInputElement>) => {
            const files = event.currentTarget.files
            if (!files) return
            const fileList = Array.from(files)
            // 同じファイルを連続で選べるよう value をリセットする
            event.currentTarget.value = ""
            await Promise.all(
                fileList.map(async file => {
                    const previewId = `preview-${previewCounterRef.current++}`
                    const previewURL = URL.createObjectURL(file)
                    setCompressingPreviews(prev => [
                        ...prev,
                        { id: previewId, objectURL: previewURL }
                    ])
                    try {
                        const compressed = await imageCompressToWebp(file, {
                            quality: 0.85,
                            maxWidth: 1500,
                            maxHeight: 1500
                        })
                        addPhoto(compressed)
                    } finally {
                        setCompressingPreviews(prev =>
                            prev.filter(p => p.id !== previewId)
                        )
                        URL.revokeObjectURL(previewURL)
                    }
                })
            )
        },
        [addPhoto]
    )

    const handleCaptureClick = useCallback(() => {
        fileInputRef.current?.click()
    }, [])

    // データ読込前は確定不可（見本写真・鍵情報を見る前に確定させない）。
    // 溢れありなら写真1枚以上が必須。溢れなしは写真不要。
    const canSubmit = useMemo(() => {
        if (submitting || isLoading) return false
        if (!isOverflowing) return true
        return photos.length > 0 && !isUploading && !hasError
    }, [
        submitting,
        isLoading,
        isOverflowing,
        photos.length,
        isUploading,
        hasError
    ])

    const handleSubmitClick = useCallback(() => {
        setOpenConfirm(true)
    }, [])

    // 報告済みの場合は再送信せずトラブル確認へ進むだけ
    const handleProceed = useCallback(() => {
        navigate(genCleaningReportPagePath(cleaningId, "ready-for-troubles"))
    }, [navigate, cleaningId])

    const handleConfirmClose = useCallback(() => {
        setOpenConfirm(false)
    }, [])

    const handleConfirm = useCallback(async () => {
        setSubmitting(true)
        try {
            // 進行中の PUT がすべて putDone / error に落ち着くまで待つ
            await waitAllSettled()

            const imageUploadIds = isOverflowing ? getReadyUploadIds() : []

            if (isOverflowing) {
                if (getHasError()) {
                    throw new Error("upload error")
                }
                if (imageUploadIds.length === 0) {
                    throw new Error("no images")
                }
            }

            await completeCheck({ isOverflowing, imageUploadIds })

            // 報告が成功したらトラブル確認へ遷移
            navigate(
                genCleaningReportPagePath(cleaningId, "ready-for-troubles")
            )
        } catch (e) {
            setSubmitting(false)
            setOpenConfirm(false)
            throwAlert(e instanceof Error ? e : new Error(String(e)), {
                errorMessage: {
                    happend: t("ゴミストッカー確認の報告に失敗しました"),
                    action: t(
                        "ネットワーク接続を確認してから、もう一度お試しください。問題が続く場合は、管理者にお問い合わせください。"
                    )
                },
                ignoreSuccess: true
            })
        }
    }, [
        waitAllSettled,
        isOverflowing,
        getReadyUploadIds,
        getHasError,
        completeCheck,
        navigate,
        cleaningId,
        throwAlert,
        t
    ])

    const header = (
        <Header {...headerProps}>
            <MobileAppHeader
                title={
                    <Typography variant="h4">
                        {t("ゴミストッカーの確認")}
                    </Typography>
                }
                onNext={openConfirmBack}
                nextIcon={<CloseIcon />}
            />
        </Header>
    )

    const backToDetailDialog = (
        <BackToDetailConfirmDialog
            type="cleaning"
            open={confirmBackOpen}
            onClose={closeConfirmBack}
            onConfirm={goBackToDetail}
        />
    )

    // 対象判定の取得に失敗した場合。報告フローを止めないようスキップ導線を出す。
    if (error) {
        return (
            <main style={mainStyle}>
                {header}
                <CenteredBody>
                    <Typography color="error">
                        {t(
                            "ゴミストッカー情報の取得に失敗しました。電波の良い場所で開き直すか、スキップして報告を続けてください。"
                        )}
                    </Typography>
                    <Button
                        variant="filled"
                        color="primary"
                        onClick={handleProceed}
                    >
                        {t("スキップして次へ進む")}
                    </Button>
                </CenteredBody>
                {backToDetailDialog}
            </main>
        )
    }

    // 対象判定の取得中はローディング表示
    if (isLoading || isTarget === undefined) {
        return (
            <main style={mainStyle}>
                <CenteredBody>
                    <Loading
                        css={css`
                            font-size: 28px;
                        `}
                    />
                </CenteredBody>
            </main>
        )
    }

    // ゴミストッカーチェック対象でない清掃はこの画面をスキップしてトラブル確認へ
    if (!isTarget) {
        return (
            <Navigate
                to={genCleaningReportPagePath(cleaningId, "ready-for-troubles")}
                replace
            />
        )
    }

    const submitLabel =
        isOverflowing && photos.length === 0
            ? t("確認完了（写真必須）")
            : t("確認完了")

    return (
        <>
            <main style={mainStyle}>
                {header}

                <div {...bodyProps}>
                    {listingName && (
                        <PropertyBar>
                            <ApartmentRoundedIcon style={{ fontSize: 18 }} />
                            {listingName}
                        </PropertyBar>
                    )}

                    <Content>
                        <InstructionBanner>
                            <AssignmentRoundedIcon style={{ fontSize: 20 }} />
                            <BannerLines>
                                <span>
                                    {t(
                                        "ゴミストッカーを目視で確認してください。"
                                    )}
                                </span>
                                <span>
                                    {t(
                                        "同じ建物のゴミストッカーは、どこか1部屋で確認すれば大丈夫です。"
                                    )}
                                </span>
                            </BannerLines>
                        </InstructionBanner>

                        {/* ゴミストッカーの情報 */}
                        <Card elevation={1}>
                            <CardTitle>
                                <TitleAccent />
                                {t("ゴミストッカーの情報")}
                            </CardTitle>
                            <GarbageStorageKeyInfo keyInfo={keyInfo} />
                        </Card>

                        {/* 見本写真 */}
                        <Card elevation={1}>
                            <CardTitle>
                                <TitleAccent />
                                {t("ゴミストッカーの見本写真")}
                            </CardTitle>
                            {sampleImageUrl ? (
                                <SampleImage
                                    src={sampleImageUrl}
                                    alt={t("ゴミストッカーの見本写真")}
                                    loading="lazy"
                                    role="button"
                                    tabIndex={0}
                                    onClick={() =>
                                        openImageViewer({
                                            id: "garbage-sample",
                                            imageUrl: sampleImageUrl
                                        })
                                    }
                                    onKeyDown={e => {
                                        if (
                                            e.key === "Enter" ||
                                            e.key === " "
                                        ) {
                                            e.preventDefault()
                                            openImageViewer({
                                                id: "garbage-sample",
                                                imageUrl: sampleImageUrl
                                            })
                                        }
                                    }}
                                />
                            ) : (
                                <EmptyState>
                                    {t("見本写真は登録されていません")}
                                </EmptyState>
                            )}
                        </Card>

                        {/* 溢れ判定 */}
                        <Card elevation={1}>
                            {alreadyReported && (
                                <ReportedBadge>
                                    <CheckCircleRoundedIcon
                                        style={{ fontSize: 14 }}
                                    />
                                    {t("報告済み")}・
                                    {dayjs
                                        .unix(existingReport.reportedAt)
                                        .format("M/D HH:mm")}
                                </ReportedBadge>
                            )}

                            <ToggleRow>
                                <Typography bolder>
                                    {t("ゴミが溢れている")}
                                </Typography>
                                <Switch
                                    checked={
                                        alreadyReported
                                            ? existingReport.isOverflowing
                                            : isOverflowing
                                    }
                                    onChange={handleToggle}
                                    disabled={alreadyReported}
                                />
                            </ToggleRow>
                            {!alreadyReported && (
                                <ToggleSubLabel>
                                    {t("溢れている場合はONにしてください")}
                                </ToggleSubLabel>
                            )}

                            {/* 報告済み: 報告した写真を表示（読み取り専用） */}
                            {alreadyReported &&
                                existingReport.isOverflowing &&
                                existingReport.images.length > 0 && (
                                    <>
                                        <PhotoSectionLabel>
                                            {t("報告した写真")}
                                            <PhotoCount>
                                                {existingReport.images.length}
                                                {t("枚")}
                                            </PhotoCount>
                                        </PhotoSectionLabel>
                                        <ThumbnailGrid>
                                            {existingReport.images.map(
                                                image => (
                                                    <ThumbnailWrapper
                                                        key={image.id}
                                                    >
                                                        <Thumbnail
                                                            src={image.url}
                                                            alt={t(
                                                                "報告済み写真を拡大表示"
                                                            )}
                                                            role="button"
                                                            tabIndex={0}
                                                            css={css`
                                                                cursor: pointer;
                                                            `}
                                                            onClick={() =>
                                                                openImageViewer(
                                                                    {
                                                                        id: image.id,
                                                                        imageUrl:
                                                                            image.url
                                                                    }
                                                                )
                                                            }
                                                            onKeyDown={e => {
                                                                if (
                                                                    e.key ===
                                                                        "Enter" ||
                                                                    e.key ===
                                                                        " "
                                                                ) {
                                                                    e.preventDefault()
                                                                    openImageViewer(
                                                                        {
                                                                            id: image.id,
                                                                            imageUrl:
                                                                                image.url
                                                                        }
                                                                    )
                                                                }
                                                            }}
                                                        />
                                                    </ThumbnailWrapper>
                                                )
                                            )}
                                        </ThumbnailGrid>
                                    </>
                                )}

                            {!alreadyReported && isOverflowing && (
                                <>
                                    <WarningBanner>
                                        <WarningAmberRoundedIcon
                                            style={{ fontSize: 20 }}
                                        />
                                        <span>
                                            {t(
                                                "ゴミ回収依頼を送信します。状況の写真を1枚以上撮影してください。"
                                            )}
                                        </span>
                                    </WarningBanner>

                                    <PhotoSectionLabel>
                                        {t("写真（1枚以上必須）")}
                                        {(photos.length > 0 ||
                                            compressingPreviews.length > 0) && (
                                            <PhotoCount>
                                                {t("撮影済み")}{" "}
                                                {photos.length +
                                                    compressingPreviews.length}
                                                {t("枚")}
                                            </PhotoCount>
                                        )}
                                    </PhotoSectionLabel>

                                    {(photos.length > 0 ||
                                        compressingPreviews.length > 0) && (
                                        <ThumbnailGrid>
                                            {compressingPreviews.map(
                                                preview => (
                                                    <ThumbnailWrapper
                                                        key={preview.id}
                                                    >
                                                        <Thumbnail
                                                            src={
                                                                preview.objectURL
                                                            }
                                                            alt=""
                                                        />
                                                        <StatusOverlay>
                                                            {t(
                                                                "アップロード中…"
                                                            )}
                                                        </StatusOverlay>
                                                    </ThumbnailWrapper>
                                                )
                                            )}
                                            {photos.map(photo => (
                                                <ThumbnailWrapper
                                                    key={photo.id}
                                                >
                                                    <Thumbnail
                                                        src={photo.objectURL}
                                                        alt=""
                                                    />
                                                    <RemoveButton
                                                        type="button"
                                                        aria-label={t("削除")}
                                                        onClick={() =>
                                                            removePhoto(
                                                                photo.id
                                                            )
                                                        }
                                                    >
                                                        <CloseIcon
                                                            style={{
                                                                fontSize: 16
                                                            }}
                                                        />
                                                    </RemoveButton>
                                                    {photo.status ===
                                                        "uploading" && (
                                                        <StatusOverlay>
                                                            {t(
                                                                "アップロード中…"
                                                            )}
                                                        </StatusOverlay>
                                                    )}
                                                    {photo.status ===
                                                        "error" && (
                                                        <StatusOverlay
                                                            role="button"
                                                            tabIndex={0}
                                                            onClick={() =>
                                                                retryPhoto(
                                                                    photo.id
                                                                )
                                                            }
                                                            onKeyDown={e => {
                                                                if (
                                                                    e.key ===
                                                                        "Enter" ||
                                                                    e.key ===
                                                                        " "
                                                                ) {
                                                                    e.preventDefault()
                                                                    retryPhoto(
                                                                        photo.id
                                                                    )
                                                                }
                                                            }}
                                                            css={css`
                                                                cursor: pointer;
                                                            `}
                                                        >
                                                            {t(
                                                                "失敗\nタップで再試行"
                                                            )}
                                                        </StatusOverlay>
                                                    )}
                                                </ThumbnailWrapper>
                                            ))}
                                        </ThumbnailGrid>
                                    )}

                                    <AddTile
                                        type="button"
                                        onClick={handleCaptureClick}
                                    >
                                        <AddTileIcon>
                                            <AddRoundedIcon />
                                        </AddTileIcon>
                                        {t(
                                            "ゴミストッカー全体が写るように撮影してください"
                                        )}
                                    </AddTile>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="image/*"
                                        multiple
                                        onChange={handleFileChange}
                                        style={{ display: "none" }}
                                    />
                                </>
                            )}
                        </Card>
                    </Content>
                </div>

                <ReportFooter {...footerProps}>
                    {alreadyReported ? (
                        <Button
                            variant="filled"
                            color="primary"
                            fullWidth
                            onClick={handleProceed}
                        >
                            {t("次へ進む")}
                        </Button>
                    ) : (
                        <Button
                            variant="filled"
                            color={canSubmit ? "primary" : undefined}
                            fullWidth
                            disabled={!canSubmit}
                            onClick={handleSubmitClick}
                        >
                            {submitLabel}
                        </Button>
                    )}
                </ReportFooter>
            </main>

            {/* 確認モーダル */}
            <Modal
                width={"calc(100% - 32px)"}
                backdrop
                open={openConfirm}
                body={
                    <Typography>
                        {isOverflowing
                            ? t(
                                  "ゴミストッカーが溢れている状態で報告します。よろしいですか？"
                              )
                            : t(
                                  "ゴミストッカーの確認を完了します。よろしいですか？"
                              )}
                    </Typography>
                }
                footer={
                    <Layout.Row gap={1} justify="space-around">
                        <Button
                            variant="filled"
                            onClick={handleConfirmClose}
                            disabled={submitting}
                        >
                            {t("キャンセル")}
                        </Button>
                        <Button
                            variant="filled"
                            color="primary"
                            onClick={handleConfirm}
                            disabled={submitting}
                        >
                            {t("確認完了")}
                        </Button>
                    </Layout.Row>
                }
            />

            {/* 見本写真フルスクリーン表示 */}
            <ImageViewerOverlay
                openedImage={openedImage}
                onClose={closeImageViewer}
            />

            {/* 清掃詳細画面に戻る確認モーダル */}
            {backToDetailDialog}
        </>
    )
}
